import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useForm } from "react-hook-form";
import { apiUrl } from "../../../constants";
import defaultValues from "../../../helpers/defaultValuesForm";
import Form from "../form/Form";
import Button from "../../button/Button";
import "./UpdateForm.css";
import FunctionalButton from "../../button/FunctionalButton";
import removeSpacesFromUrl from "../../../helpers/removeSpacesFromUrl";

const UpdateForm = ({ response, id, toggleShowModifyForm }) => {
  const [sidebarDownloads, setSidebarDownloads] = useState(false);
  const [sidebarPreviews, setSidebarPreviews] = useState(false);
  const [sidebarExtraSearchOptions, setsidebarExtraSearchOptions] =
    useState(false);
  const [overlayActive, setOverlayActive] = useState(false);
  const [errorMessage, toggleErrorMessage] = useState(false);
  const [success, toggleSuccess] = useState(false);
  const [specsheetDelete, toggleSpecsheetDelete] = useState(false);
  const [specsheetUpdate, toggleSpecsheetUpdate] = useState(true);
  const [positionButton, setPositionButton] = useState(1);

  const [updatedId, setUpdatedId] = useState("");
  const [active, setActive] = useState("");
  const [titleSpecsheetURL, setTitleSpecsheetUrl] = useState("");
  const [titleTemplate, setTitleTemplate] = useState("");

  // The manageCreatives is an object next tot the form that is used to show and hide buttons to help display the form. This object is updated on button clicks but also when getting the data from the database and fill it in the form.
  const [manageCreatives, setManageCreatives] = useState([]);

  const [secondDeleteButton, toggleSecondDeleteButton] = useState(false);
  const { control, register, handleSubmit, errors, reset } = useForm({
    defaultValues,
  });

  let navigate = useNavigate();

  // Here we reset the success when (re)loading the page.
  useEffect(() => {
    async function resetSuccess() {
      toggleSuccess(false);
      toggleSpecsheetUpdate(false);
      toggleSpecsheetDelete(false);
    }
    resetSuccess();
  }, []);

  // This is the gray overlay behind the sidebars and will deactivate the sidebars on click
  const resetOverlay = () => {
    setOverlayActive(false);
    setSidebarPreviews(false);
    setSidebarDownloads(false);
    setsidebarExtraSearchOptions(false);
  };

  // This will show or hide the downloads sidebar
  const showSidebarDownloads = () => {
    setSidebarDownloads(!sidebarDownloads);
    setOverlayActive(!overlayActive);
  };

  // This will show or hide the keywords sidebar
  const showSidebarExtraSearchOptions = () => {
    setsidebarExtraSearchOptions(!sidebarExtraSearchOptions);
    setOverlayActive(!overlayActive);
  };

  // This will show or hide the previews sidebar
  const showSidebarPreviews = () => {
    setSidebarPreviews(!sidebarPreviews);
    setOverlayActive(!overlayActive);
  };

  // Here we insert the updated data into the database when the token is valid and set the id active state and template name in order to be able to get the updated form on the screen after a button click.
  async function onSubmit(data) {
    const jwtToken = localStorage.getItem("token");
    toggleErrorMessage(false);
    try {
      const result = await axios.put(
        `${apiUrl}/specsheets/${id}`,
        {
          specsheet: data,
        },
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      setUpdatedId(result.data.updatedId);
      setActive(result.data.active);
      setTitleTemplate(result.data.templateName);

      toggleSuccess(true);
      toggleSpecsheetUpdate(true);
    } catch (e) {
      console.error(e);
      toggleErrorMessage(true);
    }
  }

  // When we have the specsheet data from the database we insert it into the form.
  useEffect(() => {
    async function prefillForm() {
      reset(response);
      setManageCreatives(response.creatives);
    }
    if (response) {
      prefillForm();
    }
  }, [response]);

  // Here we start the delete function and activate display the functionality of the deleteSpecsheetFinal
  function deleteSpecsheet() {
    toggleSecondDeleteButton(true);
    toggleSuccess(true);
  }

  // Here we return to the form when we don't want to delete the specsheet
  function returnToForm() {
    toggleSecondDeleteButton(false);
    toggleSuccess(false);
  }

  // Here we delete the specsheet
  function deleteSpecsheetFinal() {
    const jwtToken = localStorage.getItem("token");

    try {
      const result = axios.delete(`${apiUrl}/specsheets/${id}`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });
    } catch (e) {
      console.error(e);
    }
    toggleSpecsheetDelete(true);
    toggleSecondDeleteButton(false);
  }

  // After inserting the updated specsheet we can refresh the page if the active state doesn't change. If the active state does change we navigate to the new url and toggle the updateForm to false.
  function displayUpdatedSpecsheet() {
    if (active === response.active) {
      window.location.reload(false);
    } else {
      navigate(`/specsheet/${active}/${updatedId}/${titleSpecsheetURL}`);
      toggleShowModifyForm(false);
    }
  }

  // Here we set the name of the specsheet to be displayed in the url
  useEffect(() => {
    setTitleSpecsheetUrl(removeSpacesFromUrl(titleTemplate));
  }, [titleTemplate]);

  return (
    <div className="update-form-container">
      {!success && (
        <div>
          <div className="specsheet-form">
            <div className="form-introduction-wrapper">
              <h1>Update specsheet form</h1>
              <p>All fields marked with a * are required</p>
              {response && (
                <button
                  className="delete-specsheet-button"
                  onClick={deleteSpecsheet}
                >
                  DELETE SPECSHEET: {response.templateName}
                </button>
              )}
            </div>

            <form className="update-form" onSubmit={handleSubmit(onSubmit)}>
              {manageCreatives.length > 0 && (
                <Form
                  {...{
                    control,
                    register,
                    errors,
                    showSidebarDownloads,
                    showSidebarExtraSearchOptions,
                    showSidebarPreviews,
                    sidebarExtraSearchOptions,
                    sidebarPreviews,
                    sidebarDownloads,
                    setOverlayActive,
                    setManageCreatives,
                    manageCreatives,
                    positionButton,
                    setPositionButton,
                  }}
                />
              )}

              {errorMessage && (
                <p className="error">
                  Something went wrong, please try again later.
                </p>
              )}

              <div className="submit-button-wrapper">
                <section className="submit-button-section">
                  <Button type="submit">SUBMIT FORM</Button>
                </section>
              </div>
            </form>
          </div>
        </div>
      )}

      {specsheetUpdate && (
        <div className="update-success">
          <p>The specsheet update is inserted into the database.</p>
          <FunctionalButton type="button" handleClick={displayUpdatedSpecsheet}>
            Show updated specsheet
          </FunctionalButton>
        </div>
      )}

      {specsheetDelete && (
        <p className="delete-success">
          The specsheet is deleted from the database.
        </p>
      )}

      {secondDeleteButton && (
        <div className="delete-check-container">
          <div className="delete-check">
            <div>
              Are you sure you would like to delete the specsheet:
              {" " + response.templateName} ?
            </div>
            <button
              type="button"
              className="delete-button"
              onClick={deleteSpecsheetFinal}
            >
              YES, DELETE
            </button>

            <div className="return-button-container">
              <FunctionalButton type="button" handleClick={returnToForm}>
                NO, RETURN TO FORM
              </FunctionalButton>
            </div>
          </div>
        </div>
      )}

      <div
        className={overlayActive ? "overlay-visible" : "overlay"}
        onClick={resetOverlay}
      ></div>
    </div>
  );
};

export default UpdateForm;
